<script setup>
import debounce from 'lodash/debounce';
import analyticEventKeys from '@/constants/analytic-event-keys';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },

  name: {
    required: true,
    type: String,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  iconName: {
    type: String,
    default: undefined,
  },

  oppositeLocationId: {
    type: Number,
    default: undefined,
  },

  roundedLeft: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:modelValue', 'click']);

const { t } = useI18n();

const analytics = useGoogleAnalytics();
const searchStore = useSearchStore();

const countryCode = computed(() => props.modelValue?.country?.id);

const inputRef = ref(null);
const dropdownRef = ref(null);
const inputValue = ref('');
const isInputFocused = ref(false);

const inputModel = computed({
  get() {
    return isInputFocused.value ? inputValue.value : props.modelValue?.name;
  },
  set(value) {
    inputValue.value = String(value);
  },
});

function focusInput() {
  if (!inputRef.value) return;
  inputRef.value.focus();
  setTimeout(handleInputFocus);
}
function handleInputFocus() {
  isInputFocused.value = true;
}
function handleInputClickOutside() {
  setTimeout(() => {
    isInputFocused.value = false;
  });
}

onMounted(() => {
  onClickOutside(inputRef, handleInputClickOutside);
});

watch(isInputFocused, () => {
  inputModel.value = '';
  stationsRefresh?.();
});

const { data: citiesData, refresh: stationsRefresh } = useLazyAsyncData(
  useId(),
  () => {
    if (!inputValue.value) return [];

    return searchStore.fetchLocations({
      filter: {
        name: inputValue.value,
        oppositeLocationId: props.oppositeLocationId,
      },
    });
  },
  { immediate: false, deep: false, default: () => [] },
);

watch(inputValue, debounce(stationsRefresh, 250));

const recentLocations = useRecentLocations();
async function handleStationSelect(data, close) {
  await recentLocations.add(data.id);

  await analytics.pushEvent({
    event: analyticEventKeys.ENTER_SEARCH_DETAILS,
    label: `${props.name}: ${data.id}`,
  });

  emit('update:modelValue', data);
  close();
}

// Suggestions
const suggestedItems = ref([]);
watch(isInputFocused, (value) => {
  if (value && !suggestedItems.value.length) getSuggestedItems();
});
watch(() => props.oppositeLocationId, getSuggestedItems);

function getSuggestedItems() {
  searchStore
    .fetchLocations({
      filter: {
        oppositeLocationId: props.oppositeLocationId,
        isWithoutStations: true,
      },
    })
    .then((data) => (suggestedItems.value = data));
}

const items = computed(() =>
  inputValue.value ? citiesData.value : suggestedItems.value,
);

defineExpose({ focusInput });
</script>

<template>
  <UiDropdown
    ref="dropdownRef"
    class="bundles-station-search"
    :trigger-event="null"
    :opened="!!items.length && isInputFocused"
    :gap="0"
    :disabled="disabled"
    stretch-content
    use-v-if
    @click="emit('click')"
  >
    <template #trigger="{ visible }">
      <div class="bundles-station-search__trigger">
        <CIcon
          v-if="!modelValue"
          class="bundles-station-search__icon bundles-station-search__icon--trigger"
          :name="iconName"
        />

        <input
          ref="inputRef"
          :key="modelValue?.name"
          v-model="inputModel"
          class="bundles-station-search__input"
          :class="{
            'bundles-station-search__input--opened': visible,
            'bundles-station-search__input--rounded-left': roundedLeft,
            'bundles-station-search__input--with-icon': !modelValue,
          }"
          :name="name"
          autocomplete="off"
          :placeholder="placeholder"
          :title="inputModel"
          @focus="handleInputFocus"
        />

        <div class="bundles-station-search__code">
          <span
            class="bundles-station-search__font bundles-station-search__font--trigger"
          >
            {{ countryCode }}
          </span>
        </div>
      </div>
    </template>

    <template #content="{ close }">
      <div
        v-if="items.length"
        class="bundles-station-search__content"
        @click.stop
      >
        <div v-if="!inputValue" class="bundles-station-search__suggestion">
          <CIcon
            class="bundles-station-search__icon bundles-station-search__icon--suggestion"
            name="suggestions"
          />

          <span
            class="bundles-station-search__font bundles-station-search__font--suggestion"
          >
            {{ t('form.suggestions') }}
          </span>
        </div>

        <ListsCityStations
          v-for="(city, stationIndex) in items"
          :id="city.id"
          :key="stationIndex"
          class="bundles-station-search__option"
          :name="city.name"
          :original-name="city.original"
          :country="city.country"
          :stations="city.stations"
          @click="handleStationSelect($event, close)"
        />
      </div>
    </template>
  </UiDropdown>
</template>
